/**
 * applyPreset — the main orchestrator for preset application.
 *
 * Given a preset ref and user inputs, this function:
 *   1. Parses the preset manifest
 *   2. Validates all placeholder inputs
 *   3. Resolves each item ref (asset, library-instance, nested-preset, cross-item)
 *   4. Interpolates config and secrets
 *   5. Creates Instances in the Library
 *   6. Assigns the workspace to created Instances
 *   7. Stores secrets via SecretsRouter
 *   8. Returns the apply result
 *
 * Nesting: nested preset refs are recursively applied (max depth 3).
 * Outer-wins: outer preset's inputs override nested preset's defaults.
 *
 * NOTE: The `composes` field on preset items is validated at parse-time
 * (cross-item refs resolve to declared IDs) but NOT interpreted at apply-time.
 * Composition (e.g. attaching skills/connectors to an agent item) is a Phase 2
 * concern — Task 1.7 will wire the composes declarations into the Instance's
 * config as part of the agent binding step.
 *
 * @docLink packages/library/concepts#preset-application
 */

import type { SecretsRouter } from "@skaile/workspaces/secrets";
import type { PresetItem, PresetManifest } from "@skaile/workspaces/types/manifests";
import type { Assignment, IAssetIndex, Instance, PinPolicy } from "../library.js";
import { type InterpolationContext, interpolate, storeSecrets } from "./interpolate.js";
import { parsePreset, validateNestingDepth } from "./parse.js";
import { isSecretPlaceholder, validateAllPlaceholders } from "./placeholders.js";
import { resolveItem } from "./resolve-item.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Options for {@link applyPreset}.
 *
 * @docLink packages/library/concepts#preset-application
 */
export interface ApplyOpts {
  /** The library for instance creation and assignment. */
  library: IAssetIndex;
  /** Workspace ID for assignment creation. */
  workspaceId: string;
  /** Pin policy for created instances (default: "minor-track"). */
  pinPolicy?: PinPolicy;
  /** SecretsRouter for secret storage. */
  secretsRouter?: SecretsRouter;
  /** Set of known preset refs (for nested detection). */
  knownPresetRefs?: Set<string>;
  /** Loader function for nested presets (given a ref, return manifest data). */
  loadPreset?: (ref: string) => Promise<Record<string, unknown> | null>;
  /** Current nesting depth (internal, used for recursion). */
  _depth?: number;
  /** Creator identity for audit trail. */
  createdBy?: string;
}

/**
 * Aggregated result of a {@link applyPreset} call.
 *
 * @docLink packages/library/concepts#preset-application
 */
export interface ApplyResult {
  /** All instances created during this apply (including nested). */
  instances: Instance[];
  /** All assignments created during this apply. */
  assignments: Assignment[];
  /** Errors encountered (non-fatal — partial apply). */
  errors: string[];
}

/**
 * Per-item result used internally during preset application.
 *
 * @docLink packages/library/concepts#preset-application
 */
export interface ApplyItemResult {
  /** Instances created for this item. */
  instances: Instance[];
  /** Assignments created for this item. */
  assignments: Assignment[];
  /** Non-fatal errors for this item. */
  errors: string[];
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Apply a parsed preset manifest.
 *
 * This is the primary entry point for CLI and platform consumers.
 * It handles: validation → resolve → interpolate → create instances →
 * assign → store secrets.
 *
 * @param presetRef - Canonical ref of the preset being applied.
 * @param manifest - The validated preset manifest (already parsed).
 * @param inputs - User-provided placeholder values (key to value).
 * @param opts - Apply options (library, workspace, secrets, nesting context).
 * @returns Aggregated apply result.
 * @docLink packages/library/concepts#preset-application
 */
export async function applyPreset(
  presetRef: string,
  manifest: PresetManifest,
  inputs: Record<string, unknown>,
  opts: ApplyOpts,
): Promise<ApplyResult> {
  const depth = opts._depth ?? 0;
  const pinPolicy = opts.pinPolicy ?? "minor-track";

  // Validate nesting depth
  const depthError = validateNestingDepth(presetRef, depth);
  if (depthError) {
    return { instances: [], assignments: [], errors: [depthError] };
  }

  const result: ApplyResult = { instances: [], assignments: [], errors: [] };
  const itemResults = new Map<string, Instance>();

  for (const item of manifest.items) {
    const itemResult = await applyItemImpl(
      presetRef,
      item,
      inputs,
      opts,
      depth,
      pinPolicy,
      itemResults,
    );

    result.errors.push(...itemResult.errors);

    result.instances.push(...itemResult.instances);
    // Track the first instance under the item id for cross-item resolution.
    if (item.id && itemResult.instances.length > 0) {
      itemResults.set(item.id, itemResult.instances[0]);
    }
    result.assignments.push(...itemResult.assignments);
  }

  return result;
}

/**
 * Apply a preset from raw YAML data.
 *
 * Convenience wrapper that parses first, then applies.
 *
 * @param presetRef - Canonical ref of the preset.
 * @param data - Raw parsed YAML object (pre-read from disk).
 * @param inputs - User-provided placeholder values.
 * @param opts - Apply options.
 * @returns Aggregated apply result, with parse errors in `errors` if parse fails.
 * @docLink packages/library/concepts#preset-application
 */
export async function applyPresetFromData(
  presetRef: string,
  data: unknown,
  inputs: Record<string, unknown>,
  opts: ApplyOpts,
): Promise<ApplyResult> {
  const parseResult = parsePreset(data);
  if (!parseResult.ok || !parseResult.preset) {
    return {
      instances: [],
      assignments: [],
      errors: parseResult.errors.map((e) => `Parse: ${e}`),
    };
  }
  return applyPreset(presetRef, parseResult.preset, inputs, opts);
}

// ---------------------------------------------------------------------------
// Internal: per-item apply
// ---------------------------------------------------------------------------

async function applyItemImpl(
  presetRef: string,
  item: PresetItem,
  inputs: Record<string, unknown>,
  opts: ApplyOpts,
  depth: number,
  pinPolicy: PinPolicy,
  itemResults: Map<string, Instance>,
): Promise<ApplyItemResult> {
  const { library } = opts;

  // Resolve the item ref
  const resolved = await resolveItem(item, library, opts.knownPresetRefs);

  switch (resolved.kind) {
    case "cross-item":
      return applyCrossItem(resolved, itemResults);

    case "library-instance":
      return applyLibraryInstance(resolved, opts, pinPolicy);

    case "nested-preset":
      return applyNestedPreset(resolved, item, inputs, opts, depth);

    case "asset":
      return applyAssetItem(presetRef, resolved, item, inputs, opts, pinPolicy);
  }
}

// resolveItem returns a discriminated union; these helpers narrow each arm.
type ResolvedItem = Awaited<ReturnType<typeof resolveItem>>;
type CrossItemResolved = Extract<ResolvedItem, { kind: "cross-item" }>;
type LibraryInstanceResolved = Extract<ResolvedItem, { kind: "library-instance" }>;
type NestedPresetResolved = Extract<ResolvedItem, { kind: "nested-preset" }>;
type AssetResolved = Extract<ResolvedItem, { kind: "asset" }>;

function applyCrossItem(
  resolved: CrossItemResolved,
  itemResults: Map<string, Instance>,
): ApplyItemResult {
  const target = itemResults.get(resolved.targetId);
  if (!target) {
    return {
      instances: [],
      assignments: [],
      errors: [`Cross-item ref "#${resolved.targetId}" not yet resolved (items must be ordered)`],
    };
  }
  return { instances: [target], assignments: [], errors: [] };
}

async function applyLibraryInstance(
  resolved: LibraryInstanceResolved,
  opts: ApplyOpts,
  pinPolicy: PinPolicy,
): Promise<ApplyItemResult> {
  if (!resolved.instance) {
    return {
      instances: [],
      assignments: [],
      errors: [`Library instance "${resolved.instanceId}" not found`],
    };
  }
  // Assign workspace to existing instance
  try {
    const asgn = await opts.library.assign(opts.workspaceId, resolved.instanceId, pinPolicy);
    return { instances: [resolved.instance], assignments: [asgn], errors: [] };
  } catch {
    // DuplicateAssignment is non-fatal
    return { instances: [resolved.instance], assignments: [], errors: [] };
  }
}

async function applyNestedPreset(
  resolved: NestedPresetResolved,
  item: PresetItem,
  inputs: Record<string, unknown>,
  opts: ApplyOpts,
  depth: number,
): Promise<ApplyItemResult> {
  if (!opts.loadPreset) {
    return {
      instances: [],
      assignments: [],
      errors: [`Cannot resolve nested preset "${resolved.presetRef}" — no loader provided`],
    };
  }
  const nestedData = await opts.loadPreset(resolved.presetRef);
  if (!nestedData) {
    return {
      instances: [],
      assignments: [],
      errors: [`Nested preset "${resolved.presetRef}" not found`],
    };
  }
  const parseResult = parsePreset(nestedData);
  if (!parseResult.ok || !parseResult.preset) {
    return { instances: [], assignments: [], errors: parseResult.errors };
  }

  // Outer-wins: merge outer inputs over item.inputs
  const nestedInputs = {
    ...((item.inputs as Record<string, unknown>) ?? {}),
    ...inputs,
  };

  const nestedResult = await applyPreset(resolved.presetRef, parseResult.preset, nestedInputs, {
    ...opts,
    _depth: depth + 1,
  });

  // Propagate ALL nested instances and assignments (not just [0])
  return {
    instances: nestedResult.instances,
    assignments: nestedResult.assignments,
    errors: nestedResult.errors,
  };
}

/** Result of validating an asset item's placeholders into input + secret namespaces. */
interface ValidatedPlaceholders {
  validatedValues: Record<string, unknown>;
  secretValues: Record<string, string>;
  errors: string[];
}

function validateAssetPlaceholders(
  item: PresetItem,
  inputs: Record<string, unknown>,
): ValidatedPlaceholders {
  const itemInputs = { ...inputs };
  let validatedValues: Record<string, unknown> = {};
  const secretValues: Record<string, string> = {};

  if (item.placeholders && item.placeholders.length > 0) {
    const nonSecretPhs = item.placeholders.filter((ph) => !isSecretPlaceholder(ph));
    const secretPhs = item.placeholders.filter((ph) => isSecretPlaceholder(ph));

    const validation = validateAllPlaceholders(nonSecretPhs, itemInputs);
    if (!validation.valid) {
      return { validatedValues: {}, secretValues: {}, errors: validation.errors };
    }
    validatedValues = validation.values;

    const secretValidation = validateAllPlaceholders(secretPhs, itemInputs);
    if (!secretValidation.valid) {
      return { validatedValues: {}, secretValues: {}, errors: secretValidation.errors };
    }
    for (const [key, value] of Object.entries(secretValidation.values)) {
      secretValues[key] = String(value);
    }
  }

  return { validatedValues, secretValues, errors: [] };
}

async function applyAssetItem(
  presetRef: string,
  resolved: AssetResolved,
  item: PresetItem,
  inputs: Record<string, unknown>,
  opts: ApplyOpts,
  pinPolicy: PinPolicy,
): Promise<ApplyItemResult> {
  const { library } = opts;

  // Validate and interpolate
  const validated = validateAssetPlaceholders(item, inputs);
  if (validated.errors.length > 0) {
    return { instances: [], assignments: [], errors: validated.errors };
  }

  // Interpolate
  const credentialRefPrefix = `preset/${presetRef}/${item.id ?? resolved.defRef}`;
  const context: InterpolationContext = {
    inputs: validated.validatedValues,
    secrets: validated.secretValues,
  };
  const interpolated = interpolate(
    item.materialize,
    item.config as Record<string, unknown> | undefined,
    context,
    credentialRefPrefix,
  );

  // Store secrets
  if (opts.secretsRouter && interpolated.secretsToStore.length > 0) {
    try {
      await storeSecrets(opts.secretsRouter, interpolated.secretsToStore);
    } catch (err) {
      return {
        instances: [],
        assignments: [],
        errors: [
          `Secret storage failed for "${item.ref}": ${err instanceof Error ? err.message : String(err)}`,
        ],
      };
    }
  }

  // Create Instance in library
  try {
    const instance = await library.createInstance({
      defRef: resolved.defRef,
      defPin: pinPolicy,
      config: interpolated.config,
      credentialRef: interpolated.credentialRef,
      createdBy: opts.createdBy ?? `preset:${presetRef}`,
    });

    // Assign workspace
    const assignments: Assignment[] = [];
    try {
      const asgn = await library.assign(opts.workspaceId, instance.id, pinPolicy);
      assignments.push(asgn);
    } catch {
      // DuplicateAssignment is non-fatal
    }

    return { instances: [instance], assignments, errors: [] };
  } catch (err) {
    return {
      instances: [],
      assignments: [],
      errors: [
        `Instance creation failed for "${item.ref}": ${err instanceof Error ? err.message : String(err)}`,
      ],
    };
  }
}
